import { useState, useMemo } from "react";
import { ArrowUpCircle, Edit, Receipt, Eye } from "lucide-react";
import styles from "./OutgoingPayments.module.css";
import ViewPaymentModal from "../../../../components/Finance/Payments/ViewPaymentModal";
import SelectBox from "../../../../components/BaseComponents/SelectBox/SelectBox";
import RegisterPaymentModal from "../../../../components/Finance/Payments/RegisterPaymentModal/RegisterPaymentModal";
import GenerateReceiptModal from "../../../../components/Finance/Payments/GenerateReceiptModal";

const OutgoingPayments = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [componentFilter, setComponentFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);

  const [payments] = useState([
    {
      id: 1,
      supplierName: "Hotelbeds",
      supplierEmail: "payments@hotelbeds.com",
      bookingRef: "BK-2024-001234",
      componentType: "Hotel",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
      amount: 980.00,
      currency: "EUR",
      date: "2024-03-10",
      status: "Completed",
      reference: "PAY-OUT-20240310-001",
      notes: "Hotel reservation payment"
    },
    {
      id: 2,
      supplierName: "Airtuerk",
      supplierEmail: "finance@airtuerk.com",
      bookingRef: "BK-2024-001235",
      componentType: "Flight",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "SEPA",
      amount: 1450.00,
      currency: "EUR",
      date: "2024-03-09",
      status: "Pending",
      reference: "PAY-OUT-20240309-003",
      notes: "Flight tickets batch payment"
    },
    {
      id: 3,
      supplierName: "Diana Travel",
      supplierEmail: "accounts@dianatravel.com",
      bookingRef: "BK-2024-001220",
      componentType: "Hotel",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
      amount: 1720.00,
      currency: "EUR",
      date: "2024-03-08",
      status: "Completed",
      reference: "PAY-OUT-20240308-007",
      notes: "Hotel booking confirmation"
    },
    {
      id: 4,
      supplierName: "World2Meet",
      supplierEmail: "pay@world2meet.com",
      bookingRef: "BK-2024-001198",
      componentType: "Transfer",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
      amount: 320.00,
      currency: "EUR",
      date: "2024-03-07",
      status: "Completed",
      reference: "PAY-OUT-20240307-004",
      notes: "Airport transfer service"
    },
    {
      id: 5,
      supplierName: "Allianz Travel",
      supplierEmail: "agency@allianz.com",
      bookingRef: "BK-2024-001187",
      componentType: "Insurance",
      account: "Credit Card Processing",
      accountId: 3,
      paymentMethod: "Visa",
      amount: 245.00,
      currency: "EUR",
      date: "2024-03-06",
      status: "Failed",
      reference: "PAY-OUT-20240306-002",
      notes: "Insurance premium payment failed"
    },
    {
      id: 6,
      supplierName: "Europcar",
      supplierEmail: "b2b@europcar.com",
      bookingRef: "BK-2024-001156",
      componentType: "Car Rental",
      account: "Main Business Account",
      accountId: 1,
      paymentMethod: "Bank Transfer",
      amount: 580.00,
      currency: "EUR",
      date: "2024-03-05",
      status: "Completed",
      reference: "PAY-OUT-20240305-009",
      notes: "Car rental 7 days"
    }
  ]);

  const loading = false;
  const error = null;
  const saveLoading = false;

  const getStatusBadgeClass = (status) => {
    const baseClass = styles.statusBadge;
    switch (status) {
      case "Completed":
        return `${baseClass} ${styles.statusCompleted}`;
      case "Pending":
        return `${baseClass} ${styles.statusPending}`;
      case "Failed":
        return `${baseClass} ${styles.statusFailed}`;
      default:
        return baseClass;
    }
  };

  const getComponentBadgeClass = (type) => {
    const baseClass = styles.componentBadge;
    switch (type) {
      case "Flight":
        return `${baseClass} ${styles.componentFlight}`;
      case "Hotel":
        return `${baseClass} ${styles.componentHotel}`;
      case "Transfer":
        return `${baseClass} ${styles.componentTransfer}`;
      case "Insurance":
        return `${baseClass} ${styles.componentInsurance}`;
      case "Car Rental":
        return `${baseClass} ${styles.componentCarRental}`;
      case "Visa":
        return `${baseClass} ${styles.componentVisa}`;
      default:
        return baseClass;
    }
  };

  const filteredRows = useMemo(() => {
    return payments.filter((row) => {
      const matchesSearch = search === "" ||
        row.bookingRef.toLowerCase().includes(search.toLowerCase()) ||
        row.supplierName.toLowerCase().includes(search.toLowerCase()) ||
        row.reference.toLowerCase().includes(search.toLowerCase());

      const matchesStatus = statusFilter === "" || row.status === statusFilter;
      const matchesMethod = methodFilter === "" || row.paymentMethod === methodFilter;
      const matchesComponent = componentFilter === "" || row.componentType === componentFilter;
      const matchesDateFrom = dateFrom === "" || row.date >= dateFrom;
      const matchesDateTo = dateTo === "" || row.date <= dateTo;

      return matchesSearch && matchesStatus && matchesMethod && matchesComponent && matchesDateFrom && matchesDateTo;
    });
  }, [payments, search, statusFilter, methodFilter, componentFilter, dateFrom, dateTo]);

  const completedAmount = filteredRows
    .filter(p => p.status === "Completed")
    .reduce((sum, p) => sum + p.amount, 0);
  const pendingAmount = filteredRows
    .filter(p => p.status === "Pending")
    .reduce((sum, p) => sum + p.amount, 0);
  const failedAmount = filteredRows
    .filter(p => p.status === "Failed")
    .reduce((sum, p) => sum + p.amount, 0);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleRegister = () => {
    setMode("add");
    setSelectedRow(null);
    setShowRegisterModal(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowRegisterModal(true);
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setShowViewModal(true);
  };

  const handleSave = async (payload) => {
    try {
      if (mode === "add") {
        console.log("Register supplier payment:", payload);
      } else if (selectedRow?.id) {
        console.log("Update supplier payment:", selectedRow.id, payload);
      }
      setShowRegisterModal(false);
    } catch (err) {
      console.error("Failed to save supplier payment:", err);
    }
  };

  const handleGenerateReceipt = (payment) => {
    setSelectedRow(payment);
    setShowReceiptModal(true);
  };

  const handleGenerateReceiptSubmit = async (receiptData) => {
    try {
      console.log("Generating supplier receipt:", receiptData);
    } catch (err) {
      console.error("Failed to generate receipt:", err);
      throw err;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Outgoing Payments (Supplier)</div>
        <button className={styles.add} onClick={handleRegister}>
          + Register Payment
        </button>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ️</span>
        <span>Outgoing payments to suppliers. Covers flights, hotels, transfers, insurance, car rental, visa, and manual components.</span>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Total Paid</div>
          <div className={styles.summaryValue}>€{completedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className={styles.summaryCount}>{filteredRows.filter(p => p.status === "Completed").length} payments</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Pending</div>
          <div className={styles.summaryValue} style={{ color: '#f59e0b' }}>€{pendingAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className={styles.summaryCount}>{filteredRows.filter(p => p.status === "Pending").length} payments</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Failed</div>
          <div className={styles.summaryValue} style={{ color: '#ef4444' }}>€{failedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className={styles.summaryCount}>{filteredRows.filter(p => p.status === "Failed").length} payments</div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by booking, supplier, or reference..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.addbtn}>
            Search
          </button>
        </form>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={styles.dateInput}
          placeholder="From"
        />

        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={styles.dateInput}
          placeholder="To"
        />

        <SelectBox
          placeholder="Component: All"
          value={componentFilter}
          options={[
            { label: "All Components", value: "" },
            { label: "Flight", value: "Flight" },
            { label: "Hotel", value: "Hotel" },
            { label: "Transfer", value: "Transfer" },
            { label: "Insurance", value: "Insurance" },
            { label: "Car Rental", value: "Car Rental" },
            { label: "Visa", value: "Visa" },
          ]}
          onChange={(e) => setComponentFilter(e.target.value)}
        />

        <SelectBox
          placeholder="Method: All"
          value={methodFilter}
          options={[
            { label: "All Methods", value: "" },
            { label: "Bank Transfer", value: "Bank Transfer" },
            { label: "SEPA", value: "SEPA" },
            { label: "Visa", value: "Visa" },
            { label: "Mastercard", value: "Mastercard" },
          ]}
          onChange={(e) => setMethodFilter(e.target.value)}
        />

        <SelectBox
          placeholder="Status: All"
          value={statusFilter}
          options={[
            { label: "All Status", value: "" },
            { label: "Completed", value: "Completed" },
            { label: "Pending", value: "Pending" },
            { label: "Failed", value: "Failed" },
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading payments...</div>
      ) : (
        <>
          <div className={styles.meta}>
            Showing {filteredRows.length} of {payments.length} payments
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>BOOKING REF</th>
                  <th>SUPPLIER</th>
                  <th>COMPONENT</th>
                  <th>PAYMENT METHOD</th>
                  <th>ACCOUNT</th>
                  <th>AMOUNT</th>
                  <th>REFERENCE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td className={styles.date}>{row.date}</td>
                    <td>
                      <span className={styles.bookingRef}>{row.bookingRef}</span>
                    </td>
                    <td>
                      <div className={styles.supplierInfo}>
                        <div className={styles.supplierName}>{row.supplierName}</div>
                        <div className={styles.supplierEmail}>{row.supplierEmail}</div>
                      </div>
                    </td>
                    <td>
                      <span className={getComponentBadgeClass(row.componentType)}>
                        {row.componentType}
                      </span>
                    </td>
                    <td>
                      <span className={styles.badge}>{row.paymentMethod}</span>
                    </td>
                    <td className={styles.accountName}>{row.account}</td>
                    <td className={styles.amount}>
                      €{row.amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td className={styles.reference}>{row.reference}</td>
                    <td>
                      <span className={getStatusBadgeClass(row.status)}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleView(row)}
                          title="View payment"
                        >
                          <Eye size={16} />
                        </button>
                        {row.status !== "Completed" && (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleEdit(row)}
                            title="Edit payment"
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        {row.status === "Completed" && (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleGenerateReceipt(row)}
                            title="Generate receipt"
                          >
                            <Receipt size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <RegisterPaymentModal
        open={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        mode={mode}
        initialData={selectedRow}
        onSave={handleSave}
        saveLoading={saveLoading}
        paymentType="outgoing"
      />

      <ViewPaymentModal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        payment={selectedRow}
        paymentType="outgoing"
      />

      <GenerateReceiptModal
        open={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        payment={selectedRow}
        onGenerate={handleGenerateReceiptSubmit}
      />
    </div>
  );
};

export default OutgoingPayments;